import React from 'react';
import { Box, Grid, Paper, Typography, Divider, Chip } from '@mui/material';
import { useData } from '../contexts/DataContext';
import moment from 'moment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function Analytics() {
  const { estimates = [], jobs = [], customers = [] } = useData();
  const now = moment();

  // Date filters
  const isThisMonth = (date) => moment(date).isSame(now, 'month');
  const isThisYear = (date) => moment(date).isSame(now, 'year');

  // Total counts (all time)
  const totalJobs = jobs.length;
  const totalEstimates = estimates.length;
  const totalCustomers = customers.length;

  // This month counts
  const estimatesThisMonth = estimates.filter(e => e.createdAt && isThisMonth(e.createdAt));
  const jobsThisMonth = jobs.filter(j => j.createdAt && isThisMonth(j.createdAt));
  const customersThisMonth = customers.filter(c => c.createdAt && isThisMonth(c.createdAt));

  // This year counts
  const estimatesThisYear = estimates.filter(e => e.createdAt && isThisYear(e.createdAt));
  const jobsThisYear = jobs.filter(j => j.createdAt && isThisYear(j.createdAt));

  // Payment status breakdown
  const paidJobs = jobs.filter(j => j.paymentStatus === 'Paid' || j.paymentStatus === 'paid');
  const unpaidJobs = jobs.filter(j => 
    (j.paymentStatus === 'Unpaid' || j.paymentStatus === 'unpaid' || !j.paymentStatus) &&
    j.paymentStatus !== 'Paid' && j.paymentStatus !== 'paid'
  );
  const partiallyPaidJobs = jobs.filter(j => 
    j.paymentStatus === 'Partially Paid' || j.paymentStatus === 'pending'
  );

  // Revenue calculations
  const totalRevenue = jobs
    .filter(j => j.paymentStatus === 'Paid' || j.paymentStatus === 'paid')
    .reduce((sum, j) => sum + (j.total || 0), 0);

  const revenueThisMonth = jobsThisMonth
    .filter(j => j.paymentStatus === 'Paid' || j.paymentStatus === 'paid')
    .reduce((sum, j) => sum + (j.total || 0), 0);

  const outstandingAmount = jobs
    .filter(j => j.paymentStatus !== 'Paid' && j.paymentStatus !== 'paid')
    .reduce((sum, j) => {
      const remainingBalance = j.remainingBalance !== undefined 
        ? j.remainingBalance 
        : (j.total || 0) - (j.totalPaid || 0);
      return sum + Math.max(0, remainingBalance);
    }, 0);

  // Estimate conversion rate (estimates that became jobs)
  const estimatesConvertedToJobs = estimates.filter(e => 
    e.status === 'Approved' || jobs.some(j => j.estimateId === e.id)
  ).length;
  const conversionRate = totalEstimates > 0 
    ? ((estimatesConvertedToJobs / totalEstimates) * 100).toFixed(1)
    : 0;

  // Job status breakdown
  const completedJobs = jobs.filter(j => j.status === 'Completed');
  const inProgressJobs = jobs.filter(j => j.status === 'In Progress');
  const notStartedJobs = jobs.filter(j => j.status === 'Not Started');

  // Prepare monthly data for the last 6 months
  const months = Array.from({ length: 6 }, (_, i) => moment(now).subtract(5 - i, 'months'));
  const monthlyData = months.map(month => {
    const monthStr = month.format('MMM YYYY');
    const estimatesCount = estimates.filter(e => e.createdAt && moment(e.createdAt).isSame(month, 'month')).length;
    const jobsCount = jobs.filter(j => j.createdAt && moment(j.createdAt).isSame(month, 'month')).length;
    const customersCount = customers.filter(c => c.createdAt && moment(c.createdAt).isSame(month, 'month')).length;
    const revenue = jobs
      .filter(j => 
        (j.paymentStatus === 'Paid' || j.paymentStatus === 'paid') && 
        j.createdAt && 
        moment(j.createdAt).isSame(month, 'month')
      )
      .reduce((sum, j) => sum + (j.total || 0), 0);
    return {
      month: monthStr,
      Estimates: estimatesCount,
      Jobs: jobsCount,
      Customers: customersCount,
      Revenue: revenue,
    };
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
      >
        Analytics
      </Typography>

      {/* Overview Cards - Total Counts */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={3}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', height: '100%' }}>
            <WorkIcon color="primary" sx={{ fontSize: { xs: 32, sm: 48 }, mb: 1 }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Jobs Created
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {totalJobs}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total: {totalJobs} | This Month: {jobsThisMonth.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', height: '100%' }}>
            <AssessmentIcon color="secondary" sx={{ fontSize: { xs: 32, sm: 48 }, mb: 1 }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Estimates Created
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {totalEstimates}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total: {totalEstimates} | This Month: {estimatesThisMonth.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', height: '100%' }}>
            <PeopleIcon color="info" sx={{ fontSize: { xs: 32, sm: 48 }, mb: 1 }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Customers
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {totalCustomers}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This Month: +{customersThisMonth.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', height: '100%' }}>
            <TrendingUpIcon color="success" sx={{ fontSize: { xs: 32, sm: 48 }, mb: 1 }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Conversion Rate
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {conversionRate}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {estimatesConvertedToJobs} of {totalEstimates} estimates
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Financial Overview */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%', bgcolor: 'success.light' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <AttachMoneyIcon color="success" sx={{ fontSize: { xs: 32, sm: 40 } }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Revenue
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  ${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              From paid invoices
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%', bgcolor: 'primary.light' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <MonetizationOnIcon color="primary" sx={{ fontSize: { xs: 32, sm: 40 } }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Revenue This Month
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  ${revenueThisMonth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Paid in {now.format('MMMM')}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%', bgcolor: 'warning.light' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <ReceiptIcon color="warning" sx={{ fontSize: { xs: 32, sm: 40 } }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Amount Outstanding
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  ${outstandingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Unpaid invoices
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Payment Status Breakdown */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Payment Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  <Typography>Paid</Typography>
                </Box>
                <Chip label={paidJobs.length} color="success" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PendingIcon color="warning" />
                  <Typography>Partially Paid</Typography>
                </Box>
                <Chip label={partiallyPaidJobs.length} color="warning" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon color="error" />
                  <Typography>Unpaid</Typography>
                </Box>
                <Chip label={unpaidJobs.length} color="error" />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Job Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Completed</Typography>
                <Chip label={completedJobs.length} color="success" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>In Progress</Typography>
                <Chip label={inProgressJobs.length} color="info" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Not Started</Typography>
                <Chip label={notStartedJobs.length} color="default" />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Monthly Trends Chart */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Monthly Trends (Last 6 Months)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ height: { xs: 250, sm: 320 }, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlyData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="Estimates" stroke="#1976d2" strokeWidth={2} name="Estimates" />
              <Line yAxisId="left" type="monotone" dataKey="Jobs" stroke="#9c27b0" strokeWidth={2} name="Jobs" />
              <Line yAxisId="left" type="monotone" dataKey="Customers" stroke="#0288d1" strokeWidth={2} name="Customers" />
              <Line yAxisId="right" type="monotone" dataKey="Revenue" stroke="#43a047" strokeWidth={2} dot={false} name="Revenue ($)" />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* This Month vs Last Month Comparison */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              This Month Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Jobs Created:</Typography>
                <Typography variant="body2" fontWeight="bold">{jobsThisMonth.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Estimates Created:</Typography>
                <Typography variant="body2" fontWeight="bold">{estimatesThisMonth.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Customers Added:</Typography>
                <Typography variant="body2" fontWeight="bold">{customersThisMonth.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Revenue:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  ${revenueThisMonth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              This Year Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Jobs Created:</Typography>
                <Typography variant="body2" fontWeight="bold">{jobsThisYear.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Estimates Created:</Typography>
                <Typography variant="body2" fontWeight="bold">{estimatesThisYear.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total Revenue:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  ${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
} 